<script setup>
  import { reactive, toRefs, nextTick, watch } from 'vue'
  import { CloseOutlined } from '@ant-design/icons-vue'
  import { message } from 'ant-design-vue'
  import { createTerm } from '../../../http/api'

  let props = defineProps({
    sourceLangId: Number | String,
    targetLangId: Number | String,
    selectTarText: String,
    type: String,
    defaultTerm: Object,
    selectSourceText: String,
  })

  let emits = defineEmits([
    'update:isShow',
    'update:selectTarText',
    'update:selectSourceText',
    'updateTerms',
  ])

  const state = reactive({
    termList: [
      {
        source: props.selectSourceText || '',
        target: props.selectTarText || '',
      },
    ],
    loading: false,
    libraryId: '',
  })

  // 根据language获取languageName
  const languageNameFunc = (id) => {
    const _languageList = JSON.parse(localStorage.getItem('languageInfoList'))
    if (!_languageList) return
    for (let i = 0; i < _languageList.length; i++) {
      if (_languageList[i].languageId == id) {
        return _languageList[i].languageName
      }
    }
  }

  // 关闭
  const closeFunc = () => {
    emits('update:isShow', false)
    emits('update:selectTarText', '')
    emits('update:selectSourceText', '')
  }

  // 滚动到底部
  const scrollBottomFunc = () => {
    const container = document.querySelector('.scroll-box')
    const scrollHeight = container.scrollHeight
    container.scrollTop = scrollHeight
  }

  // 单个增加
  const addFunc = () => {
    if (state.termList.length >= 20) {
      message.error('最多可添加20个术语对')
      return
    }
    state.termList.push({
      source: '',
      target: '',
    })
    nextTick(() => {
      scrollBottomFunc()
    })
  }

  // 全部添加
  const addAllFunc = () => {
    const termList = state.termList
    let sourceList = []
    let targetList = []
    termList.forEach((item) => {
      sourceList.push(item.source)
      targetList.push(item.target)
    })
    if (sourceList.some((val) => !val)) {
      message.error('请输入原文')
      return
    }
    if (targetList.some((val) => !val)) {
      message.error('请输入译文')
      return
    }

    if (props.type == 'text') {
      state.loading = true
      emits('updateTerms', termList)
      closeFunc()
    } else {
      state.loading = true
      createTerm({
        libraryId: props.defaultTerm?.library_id,
        entryInfoList: termList,
      })
        .then(() => {
          message.success('添加成功')
          closeFunc()
        })
        .catch(() => {})
        .finally(() => {
          state.loading = false
        })
    }
  }

  // 删除
  const delFunc = (index) => {
    state.termList.splice(index, 1)
  }

  watch(
    () => props.selectSourceText,
    (n) => {
      state.termList[0].source = n
    }
  )
  watch(
    () => props.selectTarText,
    (n) => {
      state.termList[0].target = n
    }
  )

  const { termList, loading } = toRefs(state)
</script>

<template>
  <div class="add-term-box animate__animated animate__slideInDown">
    <div class="box-title flex-between">
      <div class="flex-align-center">
        <div class="color-333 font-weight-600 font-szie-16">添加术语</div>
        <div class="color-999 font-size-14 padding-l-16">
          语言方向：{{ languageNameFunc(sourceLangId) }} -
          {{ languageNameFunc(targetLangId) }}
        </div>
      </div>
      <CloseOutlined class="icon-close" @click="closeFunc" />
    </div>
    <perfect-scrollbar class="scroll-box">
      <div class="border-css">
        <div
          class="box-content flex-align-center"
          v-for="(item, key) in termList"
          :key="key"
        >
          <span class="margin-r-12">原文</span>
          <a-input
            v-model:value="item.source"
            placeholder="请输入原文"
            class="width-250"
          />
          <span class="margin-r-12 margin-l-20">译文</span>
          <a-input
            v-model:value="item.target"
            placeholder="请输入译文"
            class="width-250"
          />
          <!-- 删除 -->
          <div class="operation-box" v-if="key !== 0" @click="delFunc(key)">
            <svg aria-hidden="true" class="icon">
              <use xlink:href="#icon-shanchu1"></use>
            </svg>
          </div>
          <!-- 添加 -->
          <div
            class="operation-box"
            v-if="key === termList.length - 1"
            @click="addFunc"
          >
            <svg aria-hidden="true" class="icon">
              <use xlink:href="#icon-tianjia"></use>
            </svg>
          </div>
        </div>
      </div>
    </perfect-scrollbar>

    <div class="box-footer">
      <a-button type="primary" @click="addAllFunc" :loading="loading">
        添加
      </a-button>
    </div>
    <div class="tips" v-if="type == 'doc'">
      <div class="flex-align-center">
        <svg aria-hidden="true" class="icon">
          <use xlink:href="#icon-tishi1"></use>
        </svg>
        <span class="padding-l-10">
          术语将保存在【{{ defaultTerm?.library_name }}】中。
        </span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .add-term-box {
    position: fixed;
    left: calc(50% - 320px);
    top: 0;
    width: 720px;
    z-index: 1000;
    background: #fff;
    box-shadow:
      0px 4px 20px 0px rgba(0, 0, 0, 0.1),
      inset 0px -1px 0px 0px #eeeeee;
    border-radius: 8px;
  }
  .box-title {
    height: 56px;
    padding: 0 20px;
    background: #fff;
  }
  .icon-close {
    padding: 10px;
    color: #333;
  }
  .border-css {
    padding: 20px 20px 0 20px;
    border-top: 1px solid #eee;
    border-bottom: 1px solid #eee;
    max-height: 320px;
  }
  .box-content {
    padding-bottom: 20px;
    background: #fff;
  }
  .box-footer {
    height: 56px;
    padding: 12px 20px;
    float: right;
    overflow: hidden;
  }
  .width-250 {
    width: 250px;
  }
  .tips {
    position: absolute;
    left: 20px;
    bottom: 14px;
  }
  .icon {
    width: 16px;
    height: 16px;
  }
  .operation-box {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    margin-left: 10px;
    cursor: pointer;
    &:hover {
      background: #f2f3f5;
      border-radius: 4px;
    }
  }
</style>
